import { Link } from "react-router-dom";
import LegalLayout from "../components/LegalLayout";

const sections = [
  {
    id: "acceptance",
    number: "01",
    label: "Acceptance of Terms",
  },
  {
    id: "eligibility",
    number: "02",
    label: "Eligibility & Registration",
  },
  {
    id: "accounts",
    number: "03",
    label: "Account Responsibility",
  },
  {
    id: "course-access",
    number: "04",
    label: "Course Access & Usage",
  },
  {
    id: "payments",
    number: "05",
    label: "Payments & Pricing",
  },
  {
    id: "refunds",
    number: "06",
    label: "Refunds & Cancellations",
  },
  {
    id: "intellectual-property",
    number: "07",
    label: "Intellectual Property",
  },
  {
    id: "conduct",
    number: "08",
    label: "User Conduct",
  },
  {
    id: "third-parties",
    number: "09",
    label: "Third-Party Services",
  },
  {
    id: "suspension",
    number: "10",
    label: "Suspension & Termination",
  },
  {
    id: "disclaimer",
    number: "11",
    label: "Disclaimer",
  },
  {
    id: "liability",
    number: "12",
    label: "Limitation of Liability",
  },
  {
    id: "changes",
    number: "13",
    label: "Changes to Terms",
  },
  {
    id: "contact",
    number: "14",
    label: "Contact Us",
  },
];

function Section({ id, number, title, children }) {
  return (
    <section id={id} className="legal-section">
      <div className="legal-section-heading">
        <span className="legal-section-number">{number}</span>
        <h2>{title}</h2>
      </div>

      {children}
    </section>
  );
}

function TermsAndConditions() {
  return (
    <LegalLayout
      title="Terms & Conditions"
      description="These Terms & Conditions explain the rules that apply when you access CODEION, create an account, enroll in a program, make a payment, or use our learning services."
      lastUpdated="July 2026"
      sections={sections}
    >
      <div className="legal-intro">
        Please read these Terms & Conditions carefully before using CODEION.
        By accessing the platform, creating an account, enrolling in a
        program, purchasing a service, or otherwise using our website and
        learning services, you acknowledge that you have read and agree to
        these Terms & Conditions and the policies referenced below.
      </div>

      <Section
        id="acceptance"
        number="01"
        title="Acceptance of Terms"
      >
        <p>
          These Terms & Conditions govern your access to and use of the
          CODEION website, learning platform, courses, live classes,
          mentorship services, assignments, projects, digital resources,
          support services, and related features.
        </p>

        <p>
          If you do not agree with these Terms & Conditions, you should not
          access, purchase, enroll in, or use the applicable CODEION services.
        </p>
      </Section>

      <Section
        id="eligibility"
        number="02"
        title="Eligibility & Registration"
      >
        <p>
          Users must provide accurate, complete, and current information when
          registering, enrolling in a program, making a payment, or
          communicating with our support team.
        </p>

        <ul>
          <li>
            You must provide valid contact and account information.
          </li>

          <li>
            You must not impersonate another person or create an account using
            false or misleading information.
          </li>

          <li>
            If a user is legally unable to enter into a binding agreement
            independently, use of paid services should be completed with the
            involvement of a parent, guardian, or other legally authorized
            person where required by applicable law.
          </li>
        </ul>
      </Section>

      <Section
        id="accounts"
        number="03"
        title="Account Responsibility"
      >
        <p>
          You are responsible for maintaining the confidentiality and security
          of your account credentials and for activities performed through your
          account.
        </p>

        <p>
          You should promptly contact CODEION support if you believe your
          account has been accessed without authorization or if your account
          information has been compromised.
        </p>

        <div className="legal-notice">
          CODEION may request reasonable verification information when
          investigating account access, payment, enrollment, or support
          requests.
        </div>
      </Section>

      <Section
        id="course-access"
        number="04"
        title="Course Access & Usage"
      >
        <p>
          Unless explicitly stated otherwise, access to CODEION courses and
          learning resources is provided for personal, non-transferable
          educational use.
        </p>

        <ul>
          <li>
            Course access may depend on the program, batch, enrollment status,
            payment status, and announced access period.
          </li>

          <li>
            Login credentials and paid course access must not be shared with
            unauthorized users.
          </li>

          <li>
            Recording, copying, reproducing, reselling, publishing, or
            distributing protected course materials without authorization is
            prohibited.
          </li>

          <li>
            Live class schedules, mentors, course modules, assignments, and
            learning resources may be reasonably updated to improve program
            delivery.
          </li>
        </ul>
      </Section>

      <Section
        id="payments"
        number="05"
        title="Payments & Pricing"
      >
        <p>
          Prices, promotional offers, discounts, installment options, and
          available payment methods may vary between programs and may change
          before an enrollment is completed.
        </p>

        <p>
          Your enrollment is considered confirmed only after the applicable
          payment is successfully received or otherwise confirmed by CODEION.
        </p>

        <p>
          Payments may be processed through third-party payment service
          providers. Their services may also be subject to their own terms,
          privacy policies, and processing requirements.
        </p>
      </Section>

      <Section
        id="refunds"
        number="06"
        title="Refunds & Cancellations"
      >
        <p>
          Refund eligibility, refund request timelines, exclusions, and
          processing procedures are governed by the CODEION Refund Policy
          applicable at the time of purchase.
        </p>

        <p>
          Please review our{" "}
          <Link to="/refund-policy">Refund Policy</Link> before completing a
          paid enrollment.
        </p>
      </Section>

      <Section
        id="intellectual-property"
        number="07"
        title="Intellectual Property Rights"
      >
        <p>
          Unless otherwise stated, CODEION's website content, branding,
          curriculum materials, course content, videos, documents, graphics,
          software, assignments, and original learning resources are owned by
          CODEION or used with appropriate authorization.
        </p>

        <p>
          Enrollment or access to a program does not transfer ownership of
          CODEION intellectual property to the user.
        </p>
      </Section>

      <Section
        id="conduct"
        number="08"
        title="User Conduct & Prohibited Activities"
      >
        <p>
          Users must use CODEION services responsibly and must not interfere
          with the platform, instructors, mentors, other learners, or CODEION
          operations.
        </p>

        <ul>
          <li>
            Fraudulent payments, chargeback abuse, or misuse of promotional
            offers.
          </li>

          <li>
            Harassment, threats, abusive behavior, or disruption of live
            classes and learning communities.
          </li>

          <li>
            Unauthorized access attempts, malicious activity, or interference
            with platform security.
          </li>

          <li>
            Piracy, unauthorized distribution, resale, or commercial
            exploitation of protected learning materials.
          </li>
        </ul>
      </Section>

      <Section
        id="third-parties"
        number="09"
        title="Third-Party Services"
      >
        <p>
          CODEION may use third-party services for authentication, hosting,
          databases, communications, analytics, payment processing, video
          conferencing, and other operational purposes.
        </p>

        <p>
          CODEION does not control the independent operation of third-party
          services. Your use of those services may also be governed by the
          applicable provider's terms and privacy policies.
        </p>
      </Section>

      <Section
        id="suspension"
        number="10"
        title="Suspension & Termination"
      >
        <p>
          CODEION may restrict, suspend, or terminate access when reasonably
          necessary to protect users, learning resources, payment systems, the
          platform, or CODEION operations.
        </p>

        <p>
          This may include cases involving fraud, piracy, unauthorized account
          sharing, abusive conduct, security threats, repeated violations of
          these Terms, or unlawful activity.
        </p>
      </Section>

      <Section
        id="disclaimer"
        number="11"
        title="Educational Services Disclaimer"
      >
        <p>
          CODEION provides educational and skill-development services.
          Completion of a course, project, mentorship program, or certificate
          does not guarantee employment, internship selection, salary,
          promotion, examination results, or any other specific career
          outcome.
        </p>

        <p>
          Learner outcomes depend on multiple factors, including participation,
          practice, performance, experience, market conditions, and employer
          requirements.
        </p>
      </Section>

      <Section
        id="liability"
        number="12"
        title="Limitation of Liability"
      >
        <p>
          To the extent permitted by applicable law, CODEION will not be
          responsible for indirect, incidental, or consequential losses arising
          solely from the use of, inability to use, or reliance on the
          platform or educational services.
        </p>

        <p>
          Nothing in these Terms excludes or limits any liability or consumer
          right that cannot lawfully be excluded or limited under applicable
          law.
        </p>
      </Section>

      <Section
        id="changes"
        number="13"
        title="Changes to These Terms"
      >
        <p>
          CODEION may update these Terms & Conditions when necessary to reflect
          changes to our services, policies, technology, operational practices,
          or legal requirements.
        </p>

        <p>
          The updated version will be published on this page with a revised
          "Last updated" date. Where appropriate, additional notice may be
          provided through the website or other available communication
          channels.
        </p>
      </Section>

      <Section
        id="contact"
        number="14"
        title="Contact Us"
      >
        <p>
          If you have questions regarding these Terms & Conditions, your
          account, enrollment, payments, or CODEION services, contact our
          support team.
        </p>

        <p>
          Email:{" "}
          <a href="mailto:support@codeion.in">
            support@codeion.in
          </a>
        </p>

        <p>
          Support hours: Monday to Saturday, 10:00 AM to 7:00 PM IST.
        </p>
      </Section>

      <div className="legal-contact-card">
        <h2>Questions about these terms?</h2>

        <p>
          Contact the CODEION support team for assistance with your account,
          enrollment, payment, refund policy, or use of our learning services.
        </p>

        <a
          className="legal-contact-button"
          href="mailto:support@codeion.in"
        >
          Contact Support →
        </a>
      </div>
    </LegalLayout>
  );
}

export default TermsAndConditions;